// OrderInvoice.js
// UPDATED DESIGN ONLY — now matches your POS thermal receipt (80mm) look & print.
// ✅ Same APIs, same data, same info/content/sections (gift card, address, items, totals, VAT, payment, instructions, return text)
// ✅ Uses react-to-print as before

import { Link } from "react-router-dom";
import "./OrderInvoiceNew.css";
import { useReactToPrint } from "react-to-print";
import { useEffect, useRef, useState } from "react";
import useAxiosInstance from "../../../../Utilities/Hooks/useAxiosInstance";
import { ORDERS_API } from "../../../../Utilities/APIs/APIs";

function OrderInvoiceNew({ targetID }) {
  const axiosInstance = useAxiosInstance();

  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState(null);

  useEffect(() => {
    async function fetchAndSetOrder() {
      const { data } = await axiosInstance.get(
        ORDERS_API + "getSingleOrder/" + targetID,
      );
      setOrder(data[0]);
    }
    fetchAndSetOrder();
  }, [targetID, axiosInstance]);

  useEffect(() => {
    async function fetchAndSetOrderItems() {
      const { data } = await axiosInstance.get(
        ORDERS_API + "orderItemsFromOrder/" + targetID,
      );
      setOrderItems(data.orderItems);
    }
    fetchAndSetOrderItems();
  }, [axiosInstance, targetID]);

  const conponentPDF = useRef();

  const pageStyle = `
  @page { size: 80mm auto; margin: 5mm; }

  html, body {
    margin: 0 !important;
    padding: 0 !important;
    height: auto !important;
    background: #fff !important;
  }

  body {
    display: block !important;
    -webkit-text-size-adjust: 100% !important;
    text-size-adjust: 100% !important;
  }

  #print-root {
    margin: 0 !important;
    padding: 0 !important;
    /* REMOVE this unless you really need it */
    /* transform: translateY(-10mm); */
  }

  .container, .invoice_padding {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: none !important;
  }

  .oi-receipt {
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }
`;

  const generatePDF = useReactToPrint({
    content: () => conponentPDF.current,
    documentTitle: "invoice",
    pageStyle,
  });

  const discountedPrice =
    (order?.orderSubtotal - order?.promoDiscountAmount - order?.bundleDiscountAmount) * 0.1;
  const roundedDiscountedPrice = Math.round(discountedPrice);

  const hasExchange = Number(order?.previousOrderTotal || 0) > 0;

  const previousOrderTotal = Number(order?.previousOrderTotal || 0);

  const exchangeDeliveryFee = Number(order?.exchangeDeliveryCharge || 0);

  const updatedOrderTotal = Number(order?.orderTotal || 0);

  const exchangeDifference = updatedOrderTotal - previousOrderTotal;

  const isRefund = exchangeDifference < 0;

  const exchangeAmount = Math.abs(exchangeDifference);

  function getLineStatus(line) {
    const qty = Number(line.quantity || 0);

    let cancelled = Number(line.cancelQuantity || 0);

    if (!cancelled && line.isCancelled) {
      cancelled = qty;
    }

    // ITEM STATUS FIRST PRIORITY
    if (line?.itemStatus === "exchanged") {
      return "Exchanged";
    }

    if (line?.itemStatus === "refunded") {
      return "Refunded";
    }

    if (line?.itemStatus === "canceled") {
      return "Canceled";
    }

    if (
      line?.itemStatus === "delivered" ||
      line?.itemStatus === "deliveredUnpaid"
    ) {
      return "Delivered";
    }

    if (
      line?.itemStatus === "dispatchedInHouse" ||
      line?.itemStatus === "dispatchedCourier"
    ) {
      return "Dispatched";
    }

    return orderStatus;
  }

    const orderStatus = order?.orderStatus || "";
      // Thermal printers often print ৳ as ?
    const USD_RATE = 125;
    const USD_MULTIPLIER = 3;

    const isInternational =
      order?.customerCountry &&
      order?.customerCountry !== "Bangladesh" &&
      order?.customerCountry !== "BD";

    const CURRENCY_LABEL = isInternational ? "USD" : "Tk";

    const convertProductToUSD = (amount = 0) =>
      (Number(amount || 0) * USD_MULTIPLIER) / USD_RATE;

    const convertShippingToUSD = (amount = 0) =>
      Number(amount || 0) / USD_RATE;

    const money = (amount, type = "product") => {
      const num = Number(amount || 0);

      if (!Number.isFinite(num)) {
        return isInternational ? "0.00" : "0";
      }

      if (!isInternational) {
        return Math.round(num).toString();
      }

      if (type === "shipping") {
        return convertShippingToUSD(num).toFixed(0);
      }

      return convertProductToUSD(num).toFixed(0);
    };

  return (
    <div>
      <button className="submit" onClick={generatePDF}>
        Print Invoice
      </button>

      {/* PRINT AREA */}
      <div ref={conponentPDF} id="print-root">
        <div className="oi-receipt">
          {/* Inline print CSS so it always prints like POS */}
          <style>
            {`
    @page { size: 80mm auto; margin: 5mm; }
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { background: #fff; margin: 0; padding: 0; }
    @media print { .oi-hide-print { display: none !important; } }
  `}
          </style>

          {/* HEADER */}
          <div className="oi-center">
            <div className="oi-title">STRIDES CO</div>
            <div className="oi-sub">ONLINE ORDER INVOICE</div>
            <div className="oi-sub">
              BIN NUMBER: <span className="oi-strong">003429392-0109</span>
            </div>
            <div className="oi-sub oi-mushok">Mushok-6.3</div>
          </div>

          <div className="oi-hr" />

          {/* TOP INFO (kept same info) */}
          <div className="oi-row oi-small">
            <div className="oi-left">
              <span className="oi-strong">ORDER NUMBER:</span>{" "}
              {order?.invoiceNo || "-"}
            </div>
            <div className="oi-right">
              {order?.createdAt
                ? new Date(order?.createdAt).toLocaleDateString()
                : ""}
            </div>
          </div>

          <div className="oi-row oi-small">
            <div className="oi-left">
              <span className="oi-strong">ADDRESS:</span>{" "}
              {order?.shippingName || "-"}
            </div>
          </div>

          <div className="oi-row oi-small">
            <div className="oi-left">
              {order?.shippingAddress}
              {order?.customerArea ? `, ${order?.customerArea}` : ""}
            </div>
          </div>

          <div className="oi-row oi-small">
            <div className="oi-left">
              {order?.customerDistrict ? `${order?.customerDistrict} ` : ""}
              {order?.postalCode ? ` ${order?.postalCode}` : ""}, {order?.customerCountry || "Bangladesh"}
            </div>
          </div>

          {order?.orderCustomerEmail ? (
            <div className="oi-row oi-small">
              <div className="oi-left">
                <span className="oi-strong">Email:</span>{" "}
                {order?.orderCustomerEmail}
              </div>
            </div>
          ) : null}

          {order?.shippingPhone ? (
            <div className="oi-row oi-small">
              <div className="oi-left">
                <span className="oi-strong">Mobile:</span>{" "}
                {order?.shippingPhone}
              </div>
            </div>
          ) : null}

          <div className="oi-row oi-small">
            <div className="oi-left">
              <span className="oi-strong">ESTIMATED DELIVERY:</span>{" "}
              {order?.createdAt
                ? new Date(
                    new Date(order?.createdAt).setDate(
                      new Date(order?.createdAt).getDate() + 3,
                    ),
                  ).toLocaleDateString()
                : "-"}
            </div>
            <div className="oi-right oi-accent">
              {order?.gifted === "true" ? "Gift" : ""}
            </div>
          </div>

          {/* GIFT CARD INFO (kept same content) */}
          {order?.giftCardRcpName ? (
            <>
              <div className="oi-hr" />
              <div className="oi-small oi-strong oi-accent">GIFT CARD INFO</div>

              <div className="oi-row oi-small">
                <div className="oi-left">To: {order?.giftCardRcpEmail}</div>
              </div>
              <div className="oi-row oi-small">
                <div className="oi-left">
                  Recipient Name: {order?.giftCardRcpName}
                </div>
              </div>
              <div className="oi-row oi-small">
                <div className="oi-left">
                  Gift Card From: {order?.giftCardFrom}
                </div>
              </div>
              <div className="oi-row oi-small">
                <div className="oi-left">
                  Gift Card Message: {order?.giftCardMessage}
                </div>
              </div>
            </>
          ) : null}

          <div className="oi-hr" />

          {/* ITEMS HEADER */}
          <div className="oi-row oi-small oi-strong">
            <div className="oi-left">ITEMS</div>
            <div className="oi-right">AMOUNT</div>
          </div>

          {/* ITEMS LIST (same info, but receipt style) */}
          <div className="oi-items">
            {orderItems?.length > 0 ? (
              orderItems.map((orderItem) => {
                const qty = Number(orderItem?.quantity || 0);
                let cancelled = Number(orderItem?.cancelQuantity || 0);

                // backward compatibility
                if (!cancelled && orderItem?.isCancelled) {
                  cancelled = qty;
                }

                let qtyLabel = String(qty);
                if (cancelled > 0 && cancelled < qty) {
                  qtyLabel = `${qty} (Canceled ${cancelled} out of ${qty})`;
                }

                const totalPriceRaw = Number(orderItem?.totalPrice || 0);
                const unitPrice = qty > 0 ? Math.round(totalPriceRaw / qty) : 0;

                const activeQty =
                  cancelled >= qty ? 0 : Math.max(0, qty - cancelled);

                const lineTotal = unitPrice * activeQty;
                const statusLabel = getLineStatus(orderItem);

                return (
                  <div className="oi-item" key={orderItem._id}>
                    <div className="oi-item-top">
                      <div className="oi-name">
                        <span className="oi-strong">
                          {orderItem?.item?.name}
                        </span>
                        <div className="oi-sku">{orderItem?.item?.squId}</div>
                      </div>

                      {/* For gifted orders, keep “content” but hide amount/price like your current logic */}
                      {order?.gifted === "true" ? (
                        <div className="oi-price">—</div>
                      ) : (
                        <div className="oi-price">
                         {money(lineTotal, "product")} {CURRENCY_LABEL}
                        </div>
                      )}
                    </div>

                    <div className="oi-item-sub">
                      <div className="oi-meta">
                        Size: {orderItem?.size?.name}
                      </div>
                      <div className="oi-meta oi-right">
                        Qty: {qtyLabel}
                        {order?.gifted === "true"
                          ? ""
                          : ` • ${money(unitPrice, "product")} ${CURRENCY_LABEL}`}
                      </div>
                    </div>

                    <div className="oi-item-sub">
                      <div className="oi-meta">Status: {statusLabel}</div>
                      <div className="oi-meta oi-right">
                        {order?.gifted === "true" ? "" : ""}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="oi-empty">No line items found</div>
            )}
          </div>

          <div className="oi-hr" />

          {/* TOTALS (same content as your table section) */}
          {/* TOTALS */}

          {order?.gifted === "true" ? (
            <>
              <div className="oi-row oi-small">
                <div className="oi-left oi-strong">
                  Delivery ({order?.shippingMethod})
                </div>

                <div className="oi-right"></div>
              </div>

              {order?.isGiftbox ? (
                <div className="oi-row oi-small">
                  <div className="oi-left oi-strong oi-accent">Gift Box</div>

                  <div className="oi-right oi-strong">Yes</div>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="oi-row">
                <div className="oi-left">Subtotal</div>

                <div className="oi-right">
                  {money(order?.orderSubtotal, "product")} {CURRENCY_LABEL}
                </div>
              </div>

              <div className="oi-row">
                <div
                  className={`oi-left ${
                    order?.shippingMethod === "express" ? "oi-accent" : ""
                  }`}
                >
                  Delivery ({order?.shippingMethod})
                </div>

                <div className="oi-right">
                  {money(order?.shippingRate, "shipping")} {CURRENCY_LABEL}
                </div>
              </div>

              {Number(order?.promoDiscountAmount || 0) > 0 && (
                <div className="oi-row">
                  <div className="oi-left">Discount</div>

                  <div className="oi-right">
                    -{money(order?.promoDiscountAmount || 0, "product")} {CURRENCY_LABEL}
                  </div>
                </div>
              )}

            {Number(order?.bundleDiscountAmount || 0) > 0 && (
                <div className="oi-row">

                  <div className="oi-left">
                    Bundle Discount
                    {order?.bundleOffers?.length
                      ? ` (${order.bundleOffers
                          .map((offer) => offer?.name)
                          .filter(Boolean)
                          .join(" · ")})`
                      : order?.bundleOfferName
                        ? ` (${order.bundleOfferName})`
                        : ""}
                  </div>

                  <div className="oi-right">
                    -{money(order?.bundleDiscountAmount || 0, "product")} {CURRENCY_LABEL}
                  </div>

                </div>
              )}

              {order?.isGiftbox ? (
                <div className="oi-row">
                  <div className="oi-left oi-accent">Gift Box</div>

                  <div className="oi-right">
                    {isInternational ? "10.00" : "180"} {CURRENCY_LABEL}
                  </div>
                </div>
              ) : null}

              {/* NORMAL ORDER */}

              {!hasExchange && (
                <div className="oi-row oi-grand">
                  <div className="oi-left">TOTAL</div>

                  <div className="oi-right">
                       {
                          order?.displayCurrency === "USD" && order?.displayTotalUSD
                            ? `${Number(order.displayTotalUSD).toFixed(0)} USD`
                            : `${Math.round(Number(order?.orderTotal || 0))} TK`
                        }
                  </div>
                </div>
              )}

              {/* EXCHANGE ORDER */}

              {hasExchange && (
                <>
                  <div className="oi-row oi-grand">
                    <div className="oi-left">Updated Order Total</div>

                    <div className="oi-right">
                      {money(updatedOrderTotal, "product")} {CURRENCY_LABEL}
                    </div>
                  </div>

                  <div className="oi-row" style={{ marginTop: 10 }}>
                    <div className="oi-left">Customer Paid Before</div>

                    <div className="oi-right">
                      {money(previousOrderTotal, "product")} {CURRENCY_LABEL}
                    </div>
                  </div>

                  {exchangeDeliveryFee > 0 && (
                    <div className="oi-row">
                      <div className="oi-left oi-accent">
                        Exchange Delivery Fee
                      </div>

                      <div className="oi-right oi-accent">
                        {money(exchangeDeliveryFee, "shipping")} {CURRENCY_LABEL}
                      </div>
                    </div>
                  )}

                  <div
                    className="oi-row"
                    style={{
                      marginTop: 8,
                      paddingTop: 8,
                      borderTop: "1px dashed #d1d5db",
                    }}
                  >
                    <div
                      className="oi-left"
                      style={{
                        fontWeight: "bold",
                      }}
                    >
                      {isRefund ? "Customer Will Get Refund" : "Customer Will Pay"}
                    </div>

                    <div
                      className="oi-right"
                      style={{
                        fontWeight: "bold",
                       
                      }}
                    >
                      {money(exchangeAmount, "product")} {CURRENCY_LABEL}
                    </div>
                  </div>
                </>
              )}

              <div className="oi-row oi-small" style={{ marginTop: 6 }}>
                <div className="oi-left">
                  VAT (<span className="oi-strong">10%</span>) Inclusive
                </div>

                <div className="oi-right">
                  {money(roundedDiscountedPrice, "product")} {CURRENCY_LABEL}
                </div>
              </div>
            </>
          )}
          <div className="oi-hr" />

          {/* PAYMENT METHOD (same content) */}
          <div className="oi-small">
            <div className="oi-strong">Payment Method:</div>
            <div
              className={`oi-small ${
                order?.paymentMethod === "bkash" ||
                order?.paymentMethod === "SSLCommerz"
                  ? "oi-accent"
                  : ""
              }`}
            >
              {order?.paymentMethod === "cash"
                ? "Cash on Delivery"
                : order?.paymentMethod === "bkash"
                  ? "Paid by Bkash"
                  : order?.paymentMethod === "SSLCommerz"
                    ? "Paid by SSLCOMMERZ"
                    : order?.paymentMethod}
            </div>
          </div>

          {/* ADDITIONAL INSTRUCTIONS (same content) */}
          <div className="oi-hr" />
          <div className="oi-small">
            <div className="oi-strong">Additional Instructions:</div>
            <div className="oi-accent">{order?.additionalInstructions}</div>
          </div>

          {/* RETURN / EXCHANGE TEXT (same content) */}
          <div className="oi-hr" />
          <div className="oi-small oi-muted oi-footer-small">
            <div className="oi-strong" style={{ color: "#000" }}>
              Changed your mind?
            </div>
            <br />
            <div>
              Don&#39;t Worry! You can easily return or exchange your items
              (within 7 days) of delivery.
            </div>
            <div style={{ marginTop: 6 }}>
              Visit our{" "}
              <Link
                to="#"
                style={{
                  textDecoration: "none",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Returns Policy
              </Link>{" "}
              page on website, or{" "}
              <Link
                to="#"
                target="_blank"
                style={{
                  textDecoration: "none",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Contact Us
              </Link>{" "}
              for more information.
              <br />
              <br />
              Thank you for shopping on Strides Co!
            </div>
          </div>

          {/* FOOTER (same content) */}
          <div className="oi-hr" />
          <div className="oi-center oi-small oi-muted oi-footer-small">
            <div>Warm Regards</div>
            <div className="oi-strong" style={{ color: "#000" }}>
              The StridesCo Team
            </div>
            <div className="oi-strong">www.stridesco.com</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderInvoiceNew;
